/**
 * Formatting utilities for displaying data
 */

/**
 * Format a number as currency (USD)
 */
export function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '$0.00';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a number as a percentage
 * Input should be a decimal (0.5 = 50%)
 */
export function formatPercentage(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.00%';
  }

  return `${(value * 100).toFixed(2)}%`;
}

/**
 * Format a number with commas
 */
export function formatNumber(value: number | null | undefined, decimals: number = 0): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a date string
 * Input: ISO date string or timestamp
 * Output: MMM DD, YYYY
 */
export function formatDate(date: string | null | undefined): string {
  if (!date) {
    return '-';
  }

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return '-';
    }

    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
}

/**
 * Format a date string (short version)
 * Output: MM/DD
 */
export function formatDateShort(date: string | null | undefined): string {
  if (!date) {
    return '-';
  }

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return '-';
    }

    return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return '-';
  }
}

/**
 * Format compact large numbers (1K, 1M, etc.)
 */
export function formatCompactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  } else if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }

  return value.toFixed(0);
}
